import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Grid } from '@material-ui/core';

import { FetchError } from '../../../common/api/api';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import GroupSelector from '../../workflow/components/GroupSelector';
import { useUpdateFile } from '../Document.hook';
import { FileDto } from '../Document.interface';

export interface DocumentGroupSelectorProps {
  file: FileDto;
  className?: string;
  onChange?: (value: FileDto) => void;
  onError?: (error: FetchError) => void;
}

const DocumentGroupSelector: React.FC<DocumentGroupSelectorProps> = props => {
  const { file, className, onChange, onError } = props;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const fileGroupIds = useMemo(() => {
    return (file.groups || []).map(group => group.id);
  }, [file]);
  const [edit, setEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [groupIds, setGroupIds] = useState(fileGroupIds);
  const [result, updateFile] = useUpdateFile(file.id, file.isFolder);
  const { data: updatedFile, isLoading, error } = result;

  const didUpdate = useMemo(() => {
    return (
      groupIds.length !== fileGroupIds.length ||
      groupIds.some(groupId => !fileGroupIds.includes(groupId))
    );
  }, [fileGroupIds, groupIds]);

  const resetGroupIds = useCallback(() => {
    setGroupIds(fileGroupIds);
  }, [fileGroupIds]);

  // when file prop changes, reset state and group ids
  useEffect(() => {
    setEdit(false);
    setOpen(false);
    resetGroupIds();
  }, [resetGroupIds]);

  useEffect(() => {
    if (updatedFile) {
      setEdit(false);
      onChangeRef.current?.(updatedFile);
    }
  }, [updatedFile]);

  useEffect(() => {
    if (error) {
      setEdit(false);
      onErrorRef.current?.(error);
    }
  }, [error]);

  return (
    <Grid container spacing={1} className={className}>
      <Grid item xs={12}>
        <GroupSelector
          multiple
          value={groupIds}
          disabled={!edit || isLoading}
          textFieldProps={{
            label: 'Team Permissions',
            helperText: !edit
              ? undefined
              : isLoading
              ? 'Updating Team Permissions...'
              : 'Select Folder Team Permissions'
          }}
          onChange={groups => setGroupIds(groups.map(group => group.id))}
        />
      </Grid>
      {!edit ? (
        <Grid item xs={12}>
          <Button
            fullWidth
            size='small'
            color='primary'
            variant='outlined'
            disabled={isLoading}
            onClick={() => setEdit(true)}
          >
            Edit Permissions
          </Button>
        </Grid>
      ) : (
        <>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              size='small'
              variant='outlined'
              disabled={isLoading}
              onClick={() => {
                !didUpdate && setEdit(false);
                resetGroupIds();
              }}
            >
              {didUpdate ? 'Revert' : 'Cancel'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              size='small'
              color='primary'
              variant='contained'
              disabled={!didUpdate || isLoading}
              onClick={() => setOpen(true)}
            >
              Publish
            </Button>
          </Grid>
        </>
      )}

      <ConfirmDialog
        open={open}
        title='Publish Folder Team Permissions'
        confirmLabel='Publish'
        message='Are you sure you want to update the permissions?'
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          updateFile({ groupIds });
        }}
      />
    </Grid>
  );
};

export default DocumentGroupSelector;
